import { states, countries } from './parameters';

// Helper function to normalize strings for comparison
const normalizeString = (str) => {
    return str.toLowerCase().trim();
};

// Helper function to get full country name from abbreviation
const getCountryName = (countryAbbrev) => {
    const country = countries.find(c => c.code === countryAbbrev);
    return country ? country.name : countryAbbrev;
};

// Helper function to get state details by locationId
const getStateByLocationId = (locationId) => {
    return states.find(state => state.locationId === Number(locationId));
};

// Helper function to get city details by locationId and stateId
const getCityByLocationId = (cityName, stateId) => {
    const state = getStateByLocationId(stateId);
    if (!state) return null;
    return state.cities.find(city => city.name === cityName);
};

// Function to parse location from API response
export const parseLocationFromResponse = (addressData) => {
    if (!addressData) return null;

    try {
        const countryId = addressData.Country?.[0]?.$?.LID;
        const stateId = addressData.State?.[0]?.$?.LID;
        const cityName = addressData.City?.[0];

        // Get state details
        const state = getStateByLocationId(stateId);
        if (!state) return addressData.Location?.[0] || null;

        // Get country name
        const countryName = getCountryName(state.countryAbbrev);

        // If we have a city name from the response, use it directly
        if (cityName) {
            return {
                locationId: state.locationId, // We'll use state's ID since city might not match our database
                type: 'city',
                fullName: `${cityName}, ${state.stateName}, ${countryName}`,
                cityName,
                stateName: state.stateName,
                stateAbbrev: state.stateAbbrev,
                countryName,
                countryAbbrev: state.countryAbbrev,
                postalCode: addressData.PostalCode?.[0],
                rawLocation: addressData.Location?.[0]
            };
        }

        // If no city, return state level location
        return {
            locationId: state.locationId,
            type: 'state',
            fullName: `${state.stateName}, ${countryName}`,
            stateName: state.stateName,
            stateAbbrev: state.stateAbbrev,
            countryName,
            countryAbbrev: state.countryAbbrev,
            postalCode: addressData.PostalCode?.[0],
            rawLocation: addressData.Location?.[0]
        };
    } catch (error) {
        console.error('Error parsing location:', error);
        return addressData.Location?.[0] || null;
    }
};

// Function to check if a string matches the search query
const matchesQuery = (str, query) => {
    return normalizeString(str).includes(normalizeString(query));
};

// Function to get location suggestions based on user input
export const getLocationSuggestions = (searchQuery) => {
    if (!searchQuery) return [];

    const suggestions = [];

    states.forEach(state => {
        const countryName = getCountryName(state.countryAbbrev);

        // Match state name
        if (matchesQuery(state.stateName, searchQuery) ||
            matchesQuery(state.stateAbbrev, searchQuery)) {
            suggestions.push({
                value: `${state.stateName}, ${countryName}`,
                label: `${state.stateName}, ${countryName}`,
                locationId: state.locationId,
                type: 'state',
                countryName,
                countryAbbrev: state.countryAbbrev
            });
        }

        // Match cities
        state.cities.forEach(city => {
            if (matchesQuery(city.name, searchQuery)) {
                suggestions.push({
                    value: `${city.name}, ${state.stateName}, ${countryName}`,
                    label: `${city.name}, ${state.stateName}, ${countryName}`,
                    locationId: city.locationId,
                    type: 'city',
                    countryName,
                    countryAbbrev: state.countryAbbrev
                });
            }
        });
    });

    return suggestions;
};

// Function to parse a location string into structured data
export const parseLocation = (locationString) => {
    if (!locationString) return null;

    // Try to match against our location database
    const allSuggestions = getLocationSuggestions(locationString);
    const exactMatch = allSuggestions.find(suggestion =>
        normalizeString(suggestion.value) === normalizeString(locationString)
    );

    if (exactMatch) {
        return {
            locationId: exactMatch.locationId,
            type: exactMatch.type,
            fullName: exactMatch.value,
            countryName: exactMatch.countryName,
            countryAbbrev: exactMatch.countryAbbrev
        };
    }

    // If no exact match, try to parse the string
    const parts = locationString.split(',').map(part => part.trim());

    if (parts.length >= 2) {
        const cityOrState = parts[0];
        const country = parts[parts.length - 1];

        // Try to find matching state
        const matchingState = states.find(state =>
            matchesQuery(state.stateName, cityOrState) ||
            matchesQuery(state.stateAbbrev, cityOrState)
        );

        if (matchingState) {
            const countryName = getCountryName(matchingState.countryAbbrev);
            return {
                locationId: matchingState.locationId,
                type: 'state',
                fullName: `${matchingState.stateName}, ${countryName}`,
                countryName,
                countryAbbrev: matchingState.countryAbbrev
            };
        }

        // Try to find matching city
        for (const state of states) {
            const matchingCity = state.cities.find(city =>
                matchesQuery(city.name, cityOrState)
            );

            if (matchingCity) {
                const countryName = getCountryName(state.countryAbbrev);
                return {
                    locationId: matchingCity.locationId,
                    type: 'city',
                    fullName: `${matchingCity.name}, ${state.stateName}, ${countryName}`,
                    countryName,
                    countryAbbrev: state.countryAbbrev
                };
            }
        }
    }

    // If no structured match found, return the original string
    return {
        locationId: null,
        type: 'unknown',
        fullName: locationString,
        countryName: null,
        countryAbbrev: null
    };
};

// Function to get location details by ID
export const getLocationById = (locationId) => {
    if (!locationId) return null;

    // Search in states
    const matchingState = states.find(state => state.locationId === locationId);
    if (matchingState) {
        const countryName = getCountryName(matchingState.countryAbbrev);
        return {
            locationId: matchingState.locationId,
            type: 'state',
            fullName: `${matchingState.stateName}, ${countryName}`,
            countryName,
            countryAbbrev: matchingState.countryAbbrev
        };
    }

    // Search in cities
    for (const state of states) {
        const matchingCity = state.cities.find(city => city.locationId === locationId);
        if (matchingCity) {
            const countryName = getCountryName(state.countryAbbrev);
            return {
                locationId: matchingCity.locationId,
                type: 'city',
                fullName: `${matchingCity.name}, ${state.stateName}, ${countryName}`,
                countryName,
                countryAbbrev: state.countryAbbrev
            };
        }
    }

    return null;
};
